// Owns transient media drafts and editor controls; shared policy bounds accepted exports.
import {
  FILTERS,
  outputSize,
  MEDIA_LIMITS,
  MUSIC,
  LAYOUTS,
  mediaExportKey,
  resolveMediaLayout,
  validateMediaSelection,
} from '/media-policy.mjs';
import { REEL_PACKS } from './04-packs.mjs';
import { uiText, translate } from '/04-i18n.mjs';
import { renderFrame, decodePhoto, blobData } from './02-render.mjs';
import { recordMedia } from './03-recording.mjs';
export { REEL_PACKS, renderFrame };
const node = (tag, text, cls) => {
  const n = document.createElement(tag);
  if (text) n.textContent = text;
  if (cls) n.className = cls;
  return n;
};
const action = (label, fn) => {
  const b = node('button', label, 'secondary-button');
  b.type = 'button';
  b.addEventListener('click', fn);
  return b;
};
function choiceGroup(label, options, value, onChange) {
  const field = node('fieldset', null, 'editor-choice-group');
  field.append(node('legend', uiText(label)));
  const choices = node('div', null, 'editor-chips');
  const name = crypto.randomUUID();
  for (const [id, title] of options) {
    const l = node('label', null, 'editor-chip'),
      r = node('input');
    r.type = 'radio';
    r.name = name;
    r.value = id;
    r.checked = id === value;
    r.addEventListener('change', () => onChange(id));
    l.append(r, document.createTextNode(uiText(title)));
    choices.append(l);
  }
  field.append(choices);
  return field;
}
/** Decode a usable first frame with a finite deadline. Metadata alone is not a
 * drawable frame in Safari; explicit loading and muted playback request decoding.
 * Disposal aborts this wait and all event handlers/timers are removed on settlement. */
function decodeVideo(source, url, signal, timeoutMs) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let metadataReady = false;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', cancel);
      source.onloadedmetadata = source.onloadeddata = source.onerror = null;
      source.pause?.();
      error ? reject(error) : resolve();
    };
    const cancel = () => finish(new Error('Media editor closed.'));
    const timer = setTimeout(
      () => finish(new Error('Video preview took too long. Try another H.264 MP4 file.')),
      timeoutMs,
    );
    const frame = () => {
      if (
        metadataReady &&
        source.readyState >= 2 &&
        source.videoWidth > 0 &&
        source.videoHeight > 0
      )
        finish();
    };
    source.onerror = () =>
      finish(new Error('This browser cannot play that video codec. Export H.264 MP4 from Photos.'));
    source.onloadeddata = frame;
    source.onloadedmetadata = () => {
      if (
        !Number.isFinite(source.duration) ||
        source.duration > MEDIA_LIMITS.seconds ||
        source.duration <= 0
      ) {
        finish(new Error('Choose a video up to sixty seconds.'));
        return;
      }
      metadataReady = true;
      frame();
      if (!settled) source.play?.()?.catch?.(() => {});
    };
    signal.addEventListener('abort', cancel, { once: true });
    if (signal.aborted) return cancel();
    source.preload = 'auto';
    source.playsInline = true;
    source.muted = true;
    try {
      source.src = url;
      source.load?.();
    } catch (error) {
      finish(error);
    }
  });
}
function releaseVideo(source) {
  source.pause?.();
  source.removeAttribute?.('src');
  source.load?.();
}
/** Coordinate local previews and exports, releasing draft resources when disposed. */
export class MediaEditor {
  constructor({ reel = false, onBusy = () => {}, decodeTimeoutMs = 20000 } = {}) {
    this.entries = [];
    this.active = 0;
    this.reel = reel;
    this.onBusy = onBusy;
    this.busy = false;
    this.cancelled = false;
    this.decodeTimeoutMs = decodeTimeoutMs;
    this.decodeController = null;
    this.title = '';
    this.music = 'none';
    this.layout = 'auto';
    this.leadEntryId = null;
    this.pack = REEL_PACKS[0];
    this.previewURL = null;
    this.node = node('section', null, 'media-editor');
    this.status = node('p', null, 'subtle');
    this.status.setAttribute('role', 'status');
    this.file = node('input');
    this.file.type = 'file';
    this.file.multiple = true;
    this.file.accept = 'image/*,.heic,.heif,video/mp4,video/quicktime,video/webm';
    this.file.setAttribute(
      'aria-label',
      reel ? 'Add reel photos or a video' : 'Add photos or a video',
    );
    this.file.addEventListener('change', async () => {
      try {
        await this.addFiles([...this.file.files]);
      } catch (e) {
        this.status.textContent = uiText(e.message);
      }
      this.file.value = '';
    });
    this.body = node('div');
    this.node.append(
      node('h3', reel ? 'Build your reel' : 'Photos & video'),
      node(
        'p',
        reel
          ? '1. Choose a pack. 2. Add and order photos or one video. 3. Preview, then share.'
          : translate('media.attachmentLimit', {
              count: MEDIA_LIMITS.photos,
              seconds: MEDIA_LIMITS.seconds,
            }),
        'subtle',
      ),
    );
    if (reel) {
      const packs = node('div', null, 'reel-pack-grid');
      for (const pack of REEL_PACKS) {
        const b = action(pack.emoji + ' ' + pack.name, () => {
          this.applyPack(pack);
          for (const item of packs.children) item.setAttribute('aria-pressed', String(item === b));
        });
        b.setAttribute('aria-pressed', String(pack === this.pack));
        packs.append(b);
      }
      this.node.append(packs);
    }
    this.node.append(this.file, this.status, this.body);
    if (reel) this.applyPack(this.pack);
    this.draw();
  }
  applyPack(pack) {
    this.pack = pack;
    this.title = pack.title;
    this.music = pack.music;
    for (const entry of this.entries) {
      entry.filter = pack.filter;
      entry.ratio = 9 / 16;
    }
    this.draw();
  }
  async addFiles(files) {
    if (this.cancelled) throw new Error('Media editor closed.');
    if (this.busy) throw new Error('Wait for processing to finish.');
    const isVideo = (file) =>
      file.type.startsWith('video/') || /\.(mov|mp4|webm)$/i.test(file.name ?? '');
    validateMediaSelection(
      [
        ...this.entries.map((entry) => entry.kind),
        ...files.map((file) => (isVideo(file) ? 'video' : 'image')),
      ],
      { reel: this.reel },
    );
    const staged = [];
    const previousActive = this.active;
    this.setBusy(true);
    try {
      for (const file of files) {
        if (file.size > 60 * 1024 * 1024) throw new Error('Choose a source file under 60 MB.');
        const video = isVideo(file);
        let source, url;
        if (video) {
          url = URL.createObjectURL(file);
          source = node('video');
          const controller = new AbortController();
          this.decodeController = controller;
          try {
            await decodeVideo(source, url, controller.signal, this.decodeTimeoutMs ?? 20000);
          } catch (error) {
            releaseVideo(source);
            URL.revokeObjectURL(url);
            throw error;
          } finally {
            if (this.decodeController === controller) this.decodeController = null;
          }
        } else {
          source = await decodePhoto(file);
          if (
            (source.width || source.naturalWidth) * (source.height || source.naturalHeight) >
            40_000_000
          ) {
            source.close?.();
            throw new Error('Choose a photo under 40 megapixels.');
          }
        }
        if (!video) {
          const normalized = document.createElement('canvas'),
            size = outputSize(
              source.width || source.naturalWidth,
              source.height || source.naturalHeight,
              2048,
            );
          Object.assign(normalized, size);
          normalized.getContext('2d').drawImage(source, 0, 0, size.width, size.height);
          source.close?.();
          source = normalized;
        }
        staged.push({
          id: crypto.randomUUID(),
          kind: video ? 'video' : 'image',
          source,
          url,
          name: file.name || 'Demo photo',
          rotation: 0,
          ratio: this.reel ? 9 / 16 : 'original',
          zoom: 1,
          panX: 0.5,
          panY: 0.5,
          filter: this.reel ? this.pack.filter : 'original',
          alt: '',
          originalBytes: file.size,
        });
      }
      if (this.cancelled) throw new Error('Media editor closed.');
      this.entries.push(...staged);
      this.active = Math.max(0, this.entries.length - 1);
      this.status.textContent =
        'Ready to edit. Photo orientation is normalized; exported pixels contain no EXIF or GPS.';
      this.draw();
    } catch (error) {
      for (const entry of staged) {
        if (entry.kind === 'video') releaseVideo(entry.source);
        entry.source.close?.();
        if (entry.url) URL.revokeObjectURL(entry.url);
      }
      this.active = previousActive;
      throw error;
    } finally {
      this.setBusy(false);
    }
  }
  async addDemo(name) {
    const response = await fetch(`/assets/${name}-1280.webp`);
    if (!response.ok) throw new Error('Demo photo unavailable.');
    const blob = await response.blob();
    await this.addFiles([new File([blob], name + '.webp', { type: blob.type })]);
  }
  setBusy(value) {
    this.busy = value;
    this.file.disabled = value;
    this.onBusy(value);
    for (const b of this.body.querySelectorAll('button,input')) b.disabled = value;
  }
  draw() {
    if (!this.body) return;
    this.body.replaceChildren();
    if (!this.entries.length) {
      this.body.append(
        node('p', 'Choose your media above or try the demo photos below.', 'subtle'),
      );
      return;
    }
    const strip = node('div', null, 'media-strip');
    this.entries.forEach((entry, i) => {
      const b = action(`${i + 1}. ${entry.kind === 'video' ? 'Video' : entry.name}`, () => {
        this.active = i;
        this.draw();
      });
      b.setAttribute('aria-pressed', String(i === this.active));
      strip.append(b);
    });
    this.body.append(strip);
    const entry = this.entries[this.active],
      canvas = node('canvas', null, 'editor-preview');
    canvas.setAttribute('aria-label', 'Preview of the current media edits');
    renderFrame(canvas, entry, { edge: 640, title: this.title });
    this.body.append(canvas);
    if (!this.reel) this.drawLayout();
    if (this.reel)
      this.body.append(
        node(
          'p',
          `Shot ${this.active + 1}: ${this.pack.prompts[this.active % this.pack.prompts.length]} · ${this.pack.seconds}s per photo`,
          'subtle',
        ),
      );
    const refresh = () => {
      renderFrame(canvas, entry, { edge: 640, title: this.title });
      if (!this.reel) this.drawLayout();
    };
    const actions = node('div', null, 'button-row');
    actions.append(
      action('↻ Rotate', () => {
        entry.rotation = (entry.rotation + 90) % 360;
        refresh();
      }),
      action('Move earlier', () => {
        if (this.active > 0) {
          [this.entries[this.active - 1], this.entries[this.active]] = [
            this.entries[this.active],
            this.entries[this.active - 1],
          ];
          this.active--;
          this.draw();
        }
      }),
      action('Remove', () => {
        entry.source.close?.();
        if (entry.url) URL.revokeObjectURL(entry.url);
        this.entries.splice(this.active, 1);
        if (this.leadEntryId === entry.id) this.leadEntryId = null;
        this.active = Math.max(0, this.active - 1);
        this.draw();
      }),
    );
    this.body.append(actions);
    this.body.append(
      choiceGroup(
        'Crop',
        [
          ['original', 'Original'],
          [1, 'Square'],
          [4 / 5, 'Portrait'],
          [9 / 16, 'Reel'],
          [16 / 9, 'Wide'],
        ],
        entry.ratio,
        (value) => {
          entry.ratio = value;
          refresh();
        },
      ),
    );
    for (const [key, label, min, max, step] of [
      ['zoom', 'Zoom', 1, 3, 0.05],
      ['panX', 'Move left / right', 0, 1, 0.02],
      ['panY', 'Move up / down', 0, 1, 0.02],
    ]) {
      const l = node('label', label, 'editor-range'),
        range = node('input');
      range.type = 'range';
      Object.assign(range, { min, max, step, value: entry[key] });
      range.setAttribute('aria-label', label);
      range.addEventListener('input', () => {
        entry[key] = Number(range.value);
        refresh();
      });
      l.append(range);
      this.body.append(l);
    }
    this.body.append(
      choiceGroup(
        'Filter pack',
        Object.keys(FILTERS).map((id) => [id, id[0].toUpperCase() + id.slice(1)]),
        entry.filter,
        (value) => {
          entry.filter = value;
          refresh();
        },
      ),
    );
    const title = node('input');
    title.value = this.title;
    title.maxLength = 100;
    title.placeholder = 'Title on your media (optional)';
    title.setAttribute('aria-label', 'Media title');
    title.addEventListener('input', () => {
      this.title = title.value;
      refresh();
    });
    this.body.append(title);
    const alt = node('input');
    alt.value = entry.alt;
    alt.maxLength = 500;
    alt.placeholder = 'Describe this photo or video';
    alt.setAttribute('aria-label', 'Media description');
    alt.addEventListener('input', () => (entry.alt = alt.value));
    this.body.append(alt);
    if (!this.reel && entry.kind === 'image')
      this.body.append(
        choiceGroup(
          'Gallery layout',
          LAYOUTS.filter(
            (id) =>
              !this.entries.some((item) => item.kind === 'video') ||
              ['auto', 'carousel'].includes(id),
          ).map((id) => [
            id,
            {
              auto: 'Automatic composition',
              grid: 'Grid',
              carousel: 'Carousel',
              feature: 'Key photo',
            }[id],
          ]),
          this.layout,
          (value) => {
            this.layout = value;
            this.drawLayout();
          },
        ),
        choiceGroup(
          'Key photo',
          [
            ['auto', 'Automatic key photo'],
            ...this.entries.flatMap((item, index) =>
              item.kind === 'image' ? [[item.id, `${uiText('Photo')} ${index + 1}`]] : [],
            ),
          ],
          this.leadEntryId ?? 'auto',
          (value) => {
            this.leadEntryId = value === 'auto' ? null : value;
            this.drawLayout();
          },
        ),
      );
    this.body.append(
      choiceGroup(
        'Original CC0 music',
        MUSIC.map((id) => [
          id,
          id
            .split('-')
            .map((word) => word[0].toUpperCase() + word.slice(1))
            .join(' '),
        ]),
        this.music,
        (value) => {
          this.music = value;
        },
      ),
    );
    const audio = node('audio');
    audio.controls = true;
    audio.preload = 'none';
    const play = action('Preview chosen music', () => {
      if (this.music === 'none') {
        this.status.textContent = 'Choose a music track first.';
        return;
      }
      audio.src = `/assets/music/${this.music}.wav`;
      audio.play().catch(() => {
        this.status.textContent = 'Use the audio play control to listen.';
      });
    });
    this.body.append(play, audio);
    if (this.reel || entry.kind === 'video') {
      const preview = action('Prepare video preview', async () => {
        preview.disabled = true;
        try {
          const exported = await this.export();
          if (this.previewURL) URL.revokeObjectURL(this.previewURL);
          this.previewURL = URL.createObjectURL(exported.blob);
          const video = node('video', null, 'editor-video');
          video.controls = true;
          video.playsInline = true;
          video.preload = 'metadata';
          video.src = this.previewURL;
          this.body.append(video);
          this.status.textContent = 'Prepared. Review the video before sharing.';
        } catch (e) {
          this.status.textContent = uiText(e.message);
        } finally {
          preview.disabled = false;
        }
      });
      this.body.append(preview);
    }
  }
  /** Preview the same policy and edited pixel dimensions that the published gallery uses. */
  drawLayout() {
    this.body.querySelector('.editor-layout-preview')?.remove();
    const preview = node('div', null, 'editor-layout-preview');
    const items = node('div', null, 'media-items');
    const media = this.entries.map((entry, index) => {
      const canvas = node('canvas');
      const size = renderFrame(canvas, entry, { edge: 240 });
      canvas.setAttribute(
        'aria-label',
        `${uiText('Photo')} ${index + 1} · ${uiText('Composition preview')}`,
      );
      canvas.dataset.mediaIndex = String(index);
      items.append(canvas);
      return { ...size, kind: entry.kind };
    });
    const plan = resolveMediaLayout(media, {
      layout: this.layout,
      leadMediaIndex: this.leadMediaIndex,
    });
    preview.classList.add(`layout-${plan.layout}`, `composition-${plan.variant}`);
    items.children[plan.leadIndex]?.classList.add('media-lead');
    preview.append(node('p', uiText('Composition preview'), 'subtle'), items);
    this.body.append(preview);
  }
  get leadMediaIndex() {
    const index = this.entries.findIndex((entry) => entry.id === this.leadEntryId);
    return index < 0 ? null : index;
  }
  async export() {
    if (!this.entries.length)
      return {
        uploads: [],
        title: this.title,
        music: this.music,
        layout: this.layout,
        leadMediaIndex: this.leadMediaIndex,
      };
    if (this.busy) throw new Error('Wait for processing.');
    this.setBusy(true);
    this.cancelled = false;
    try {
      validateMediaSelection(
        this.entries.map((entry) => entry.kind),
        { reel: this.reel },
      );
      if (this.reel) {
        const key = mediaExportKey(this);
        if (this.prepared?.key === key) return this.prepared.result;
        const result = await this.record();
        this.prepared = { key, result };
        return result;
      }
      const uploads = [];
      let videoResult;
      let totalBytes = 0;
      for (const entry of this.entries) {
        if (entry.kind === 'video') {
          const draft = Object.create(this);
          draft.entries = [entry];
          const key = mediaExportKey(draft);
          videoResult =
            this.preparedVideo?.key === key
              ? this.preparedVideo.result
              : await recordMedia.call(draft);
          this.preparedVideo = { key, result: videoResult };
          totalBytes += videoResult.blob.size;
          uploads.push(videoResult.uploads[0]);
          continue;
        }
        const canvas = document.createElement('canvas'),
          size = renderFrame(canvas, entry, { edge: MEDIA_LIMITS.edge, title: this.title });
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
        if (!blob || blob.size > MEDIA_LIMITS.photoBytes)
          throw new Error('A processed photo is too large. Crop or reduce it.');
        totalBytes += blob.size;
        uploads.push({ data: await blobData(blob), ...size, alt: entry.alt });
      }
      if (this.cancelled) throw new Error('Export cancelled.');

      if (totalBytes > MEDIA_LIMITS.totalBytes)
        throw new Error('Processed media exceeds the demo size limit.');
      return {
        uploads,
        blob: videoResult?.blob,
        musicMixed: videoResult?.musicMixed ?? false,
        title: this.title,
        music: this.music,
        layout: videoResult ? 'carousel' : this.layout,
        leadMediaIndex: this.leadMediaIndex,
      };
    } finally {
      this.setBusy(false);
    }
  }
  async record() {
    return recordMedia.call(this);
  }
  destroy() {
    this.cancelled = true;
    this.decodeController?.abort();
    for (const entry of this.entries) {
      if (entry.kind === 'video') releaseVideo(entry.source);
      entry.source.close?.();
      if (entry.url) URL.revokeObjectURL(entry.url);
    }
    if (this.previewURL) URL.revokeObjectURL(this.previewURL);
    for (const audio of this.body.querySelectorAll('audio,video')) audio.pause();
  }
}
